/** Customer-facing labels for claim-readiness controls and configured zones. */

const RAW_CONTROL_ID_RE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/;

const CLAIM_READINESS_CUSTOMER_LABELS: Record<string, string> = {
  verification_method: 'Verification method',
  field_confirmation_of_zone_controls: 'Control confirmed on site',
  observation_coverage_reliability: 'Observation coverage',
  matched_window_followup_validation: 'Follow-up measurement',
  contributing_condition_status_required: 'Contributing condition review',
  closure_summary_required: 'Closure summary',
};

const VERBOSE_CLAIM_LABELS: Record<string, string> = {
  'observation coverage reliability': 'Observation coverage',
  'field confirmation of zone controls': 'Control confirmed on site',
  'matched-window follow-up validation': 'Follow-up measurement',
  'matched window follow-up validation': 'Follow-up measurement',
  'matched-window followup validation': 'Follow-up measurement',
  'follow-up measurement requirement': 'Follow-up measurement',
};

const CONFIGURED_ZONE_LABELS: Record<string, string> = {
  worksafety: 'Walkway Zone',
  demo5tes: 'Restricted Forklift Zone',
};

const CONFIGURED_CAMERA_LABELS: Record<string, string> = {
  cam_walkway_zone_demo: 'Warehouse Room B - Walkway',
  cam_a6c12b0f3d94: 'Warehouse Room A',
};

export function looksLikeRawControlId(value?: string | null): boolean {
  const text = String(value || '').trim();
  if (!text) return false;
  return RAW_CONTROL_ID_RE.test(text);
}

export function customerFacingClaimReadinessLabel(input: {
  label?: string | null;
  nextActionLabel?: string | null;
  controlId?: string | null;
}): string {
  const remap = (value: string): string | null => {
    const compact = value.trim().toLowerCase().replace(/[_]+/g, ' ');
    return VERBOSE_CLAIM_LABELS[compact] || CLAIM_READINESS_CUSTOMER_LABELS[value.trim()] || null;
  };
  const label = String(input.label || '').trim();
  const remappedLabel = remap(label);
  if (remappedLabel) return remappedLabel;
  if (label && !looksLikeRawControlId(label)) return label;
  const next = String(input.nextActionLabel || '').trim();
  const remappedNext = remap(next);
  if (remappedNext) return remappedNext;
  if (next && !looksLikeRawControlId(next)) return next;
  const id = String(input.controlId || label || '').trim();
  if (CLAIM_READINESS_CUSTOMER_LABELS[id]) return CLAIM_READINESS_CUSTOMER_LABELS[id];
  if (looksLikeRawControlId(id)) return 'Follow-up measurement';
  return label || 'Follow-up measurement';
}

export function resolveConfiguredZoneLabel(value?: string | null): string | null {
  const text = String(value || '').trim();
  if (!text) return null;
  const compact = text.toLowerCase().replace(/[_\-\s]+/g, '');
  if (compact === 'worksafety') return CONFIGURED_ZONE_LABELS.worksafety;
  if (compact === 'demo5tes' || compact === 'demotestarea') {
    return CONFIGURED_ZONE_LABELS.demo5tes;
  }
  return CONFIGURED_ZONE_LABELS[text.toLowerCase()] || null;
}

const INTERNAL_ZONE_TOKEN_RE = /\b(worksafety|demo5tes)\b/gi;

export function replaceInternalLocationTokens(value?: string | null): string {
  const text = String(value || '');
  if (!text) return text;
  return text.replace(INTERNAL_ZONE_TOKEN_RE, (match) => (
    resolveConfiguredZoneLabel(match) || match
  ));
}

export function resolveConfiguredCameraLabel(value?: string | null): string | null {
  const text = String(value || '').trim();
  if (!text) return null;
  const compact = text.toLowerCase();
  return CONFIGURED_CAMERA_LABELS[compact] || null;
}
